import React from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { PHONE_DISPLAY, PHONE_TEL_HREF } from "@/lib/contact";
import { ZONES as ZONE_PAGES } from "@/lib/zones";
import { track } from "@/lib/analytics";
import { fadeUp, fadeUpStagger, IN_VIEW } from "@/lib/motion";

const ZONES = [
  "Roma Centro", "EUR", "Tiburtina", "Prenestina", "Tuscolana",
  "Appia", "Aurelia", "Ostia", "Fiumicino", "Casilina",
  "Salaria", "Cassia", "Flaminia", "Provincia di Roma",
  "Monteverde", "Trastevere", "Quadraro", "Centocelle", "Pigneto",
];

export default function Zones() {
  return (
    <section id="zone" data-testid="zones-section" className="relative py-20 md:py-28 bg-white">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={IN_VIEW}
          variants={fadeUpStagger}
          className="max-w-2xl"
        >
          <motion.p variants={fadeUp} className="text-brand-orange text-xs sm:text-sm font-bold tracking-[0.35em] uppercase">— 04 / Zone servite</motion.p>
          <motion.h2 variants={fadeUp} className="mt-4 font-display font-black text-4xl sm:text-5xl lg:text-6xl text-navy tracking-tight leading-[1.02]">
            Copertura totale di <span className="text-brand-orange italic">Roma</span> e provincia.
          </motion.h2>
          <motion.p variants={fadeUp} className="mt-4 text-muted-foreground text-base sm:text-lg">
            Interveniamo in città e in tutta la provincia di Roma. Indicaci la posizione: arriviamo noi.
          </motion.p>
        </motion.div>

        <div className="mt-10 grid lg:grid-cols-12 gap-6 lg:gap-10 items-stretch">
          <div className="lg:col-span-5">
            <ul className="grid grid-cols-2 gap-2 sm:gap-3" data-testid="zones-list">
              {ZONES.map((z) => (
                <li
                  key={z}
                  className="flex items-center gap-2 rounded-xl border border-border bg-white px-3 py-2.5 hover:border-brand-orange/50 hover:bg-brand-orange/5 transition"
                >
                  <MapPin className="h-4 w-4 text-brand-orange" />
                  <span className="font-medium text-navy text-sm">{z}</span>
                </li>
              ))}
            </ul>

            <a
              href={PHONE_TEL_HREF}
              onClick={() => track.phoneCall("zones")}
              data-testid="zones-call-button"
              className="mt-6 inline-flex items-center gap-3 rounded-2xl bg-brand-orange text-white px-5 py-3 font-bold hover:-translate-y-0.5 hover:brightness-110 transition btn-glow-orange"
            >
              <Phone className="h-5 w-5" />
              Chiama {PHONE_DISPLAY}
            </a>
          </div>

          <div className="lg:col-span-7">
            <div className="relative w-full overflow-hidden rounded-2xl border border-border shadow-sm aspect-[4/3] sm:aspect-[16/10]">
              <iframe
                title="Mappa di Roma - Zone servite"
                data-testid="zones-map"
                src="https://www.google.com/maps?q=Roma%2C%20Italy&z=10&output=embed"
                className="absolute inset-0 w-full h-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </div>
        </div>

        {/* Dedicated zone landing pages - internal links for SEO */}
        <div className="mt-12 border-t border-border pt-10">
          <h3 className="font-display font-bold text-xl sm:text-2xl text-navy">
            Pagine dedicate per zona
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Approfondimenti per le principali zone di Roma e provincia.
          </p>
          <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3" data-testid="zone-pages-list">
            {ZONE_PAGES.map((z) => (
              <li key={z.slug}>
                <Link
                  to={`/${z.slug}`}
                  data-testid={`zone-page-link-${z.slug}`}
                  className="group flex items-center justify-between gap-2 rounded-xl border border-border bg-white px-4 py-3 hover:border-brand-orange hover:bg-brand-orange/5 transition"
                >
                  <span className="flex items-center gap-2 font-semibold text-navy text-sm">
                    <MapPin className="h-4 w-4 text-brand-orange" />
                    Carro attrezzi {z.name}
                  </span>
                  <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-brand-orange transition" />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
